import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAtomValue } from 'jotai';
import useActionDuration from '../../../../../common/hooks/useActionDuration';
import { getAnnotationStyle } from '../../../../../common/utils/utils';
import { isVideoLabelingM4EnabledAtom } from '../../../../labelling-v2/atoms/m4-atoms';
import { useStageAtoms } from '../../../../labelling-v2/components/stage-scope/hooks';
import { useDynamicAttributesText } from '../../../../labelling-v2/hooks/use-dynamic-attributes-text';
import type { ANNOTATION_SHAPE_STYLING_TYPE } from '../../../constants';
import { ANNOTATION_TYPE } from '../../../constants';
import useStage from '../../../providers/hooks/use-stage';
import { type StagePointerEventT } from '../../../providers/stage-provider';
import { type EllipseAnnotationT } from '../../../types';
import { isLabelingMode } from '../../toolbar/utils';
import Shape from '../shape/shape';
import type { VERTEX_TYPE } from '../shape/vertex';
import { getStylesForStylingType } from '../utils';
import { getDrawingEllipseCoords, getEllipseVertices, getTransformedPointsOnVertexMove, getTranslatedEllipse } from './utils';
type PropsT = {
  isDrawing?: boolean;
  annotation?: EllipseAnnotationT;
  annotationIdx?: string;
  disableChanges?: boolean;
  stylingType?: (typeof ANNOTATION_SHAPE_STYLING_TYPE)[keyof typeof ANNOTATION_SHAPE_STYLING_TYPE];
};
const Ellipse = ({
  isDrawing,
  annotation,
  annotationIdx,
  disableChanges,
  stylingType
}: PropsT) => {
  const {
    stage,
    addAnnotation,
    updateAnnotation,
    getStageDimension
  } = useStage();
  const {
    editorModeAtom,
    editableAnnotationAtom,
    focusedAnnotationIdAtom,
    selectedAnnotationLabelAtom
  } = useStageAtoms();
  const editorMode = useAtomValue(editorModeAtom);
  const editableAnnotation = useAtomValue(editableAnnotationAtom);
  const focusedAnnotationId = useAtomValue(focusedAnnotationIdAtom);
  const annotationLabel = useAtomValue(selectedAnnotationLabelAtom);
  const isVideoLabelingM4Enabled = useAtomValue(isVideoLabelingM4EnabledAtom);
  const [ellipse, setEllipse] = useState<EllipseAnnotationT | undefined | null>(annotation);
  const [labelCoords, setLabelCoords] = useState(null);
  const [initialPoint, setInitialPoint] = useState([0, 0]);
  const [selectedVertex, setSelectedVertex] = useState<(typeof VERTEX_TYPE)[keyof typeof VERTEX_TYPE] | undefined | null>(null);
  const ellipseRef = useRef<SVGEllipseElement>(null);
  const {
    start,
    end
  } = useActionDuration();
  const isLocked = Boolean(annotation?.locked);
  useEffect(() => {
    setEllipse(annotation);
  }, [annotation]);
  const isEditable = editableAnnotation === annotationIdx;
  const isFocused = focusedAnnotationId === annotationIdx;
  useEffect(() => {
    if (!isDrawing && ellipse) {
      // @ts-expect-error missing null check
      setLabelCoords([ellipse.data.centerX, ellipse.data.centerY]);
    }
  }, [isDrawing, ellipse]);

  // Initiate New Ellipse annotation on Mouse Point Down
  const onPointerDown = useCallback((stageEvent: StagePointerEventT) => {
    const {
      mousePos
    } = stageEvent.detail;

    // TODO: handle case where annotationLabel is not selected
    if (!annotationLabel) return;

    // Clamp initial point to stage bounds
    const [stageWidth, stageHeight] = getStageDimension();
    const clampedX = Math.max(0, Math.min(mousePos[0], stageWidth));
    const clampedY = Math.max(0, Math.min(mousePos[1], stageHeight));
    const initialEllipse: EllipseAnnotationT = {
      annotationLabel,
      annotationType: ANNOTATION_TYPE.ELLIPSE,
      data: {
        centerX: clampedX,
        centerY: clampedY,
        radiusX: 0,
        radiusY: 0
      }
    };
    setEllipse(initialEllipse);
    setInitialPoint([clampedX, clampedY]);
    start();
  }, [annotationLabel, start, getStageDimension]);

  // Update Ellipse on Mouse Move till Mouse Up is not called
  const onPointerMove = useCallback((stageEvent: StagePointerEventT) => {
    // If no existing ellipse - do not anything
    if (!ellipse) {
      return;
    }
    const {
      mousePos
    } = stageEvent.detail;
    const [stageWidth, stageHeight] = getStageDimension();
    setEllipse({
      ...ellipse,
      data: getDrawingEllipseCoords(initialPoint[0] ?? 0, initialPoint[1] ?? 0, mousePos[0], mousePos[1], stageWidth, stageHeight)
    });
  }, [ellipse, initialPoint, getStageDimension]);

  // On Mouse Up add annotation
  const onPointerUp = useCallback(() => {
    if (ellipse) {
      if (ellipse.data.radiusX > 0 && ellipse.data.radiusY > 0) {
        addAnnotation(ellipse, {
          actionDuration: end()
        });
      }
      setEllipse(null);
    }
  }, [ellipse, addAnnotation, end]);
  useEffect(() => {
    if (!stage || !isDrawing || !isLabelingMode(editorMode)) {
      return;
    }

    // listen to mouse events if in drawing mode
    stage.addEventListener('pointerdown', onPointerDown);
    stage.addEventListener('pointermove', onPointerMove);
    stage.addEventListener('pointerup', onPointerUp);
    return () => {
      stage.removeEventListener('pointerdown', onPointerDown);
      stage.removeEventListener('pointermove', onPointerMove);
      stage.removeEventListener('pointerup', onPointerUp);
    };
  }, [isDrawing, stage, onPointerDown, onPointerMove, onPointerUp, editorMode]);
  const onDrag = useCallback((stageEvent: StagePointerEventT) => {
    if (!annotation) {
      return;
    }
    // For move drag, if the target is not same ignore the event.
    if (!selectedVertex && stageEvent.detail.dragTarget !== ellipseRef.current) {
      return;
    }
    const [stageWidth, stageHeight] = getStageDimension();
    const newEllipse = selectedVertex ? getTransformedPointsOnVertexMove(annotation, stageEvent.detail.mousePos, selectedVertex, null, stageWidth, stageHeight) : getTranslatedEllipse(stageEvent.detail.deltaX, stageEvent.detail.deltaY, annotation, stageWidth, stageHeight);
    setEllipse(newEllipse);
    start();
  }, [selectedVertex, annotation, start, getStageDimension]);
  const onDragEnd = useCallback((stageEvent: StagePointerEventT) => {
    if (!annotation || annotationIdx === undefined) {
      return;
    }
    // For move drag, if the target is not same ignore the event.
    if (!selectedVertex && stageEvent.detail.dragTarget !== ellipseRef.current) {
      return;
    }
    const [stageWidth, stageHeight] = getStageDimension();
    const newEllipse = selectedVertex ? getTransformedPointsOnVertexMove(annotation, stageEvent.detail.mousePos, selectedVertex, null, stageWidth, stageHeight) : getTranslatedEllipse(stageEvent.detail.deltaX, stageEvent.detail.deltaY, annotation, stageWidth, stageHeight);
    setEllipse(newEllipse);
    updateAnnotation(annotationIdx, newEllipse, {
      actionDuration: end()
    });
    setSelectedVertex(null);
  }, [selectedVertex, annotation, annotationIdx, updateAnnotation, end, getStageDimension]);
  useEffect(() => {
    if (!stage || !isEditable || disableChanges || isLocked || !isLabelingMode(editorMode)) {
      return;
    }
    stage.addEventListener('drag', onDrag);
    stage.addEventListener('dragend', onDragEnd);
    return () => {
      stage.removeEventListener('drag', onDrag);
      stage.removeEventListener('dragend', onDragEnd);
    };
  }, [isEditable, stage, onDrag, onDragEnd, disableChanges, editorMode, isLocked]);
  const dynamicAttributesText = useDynamicAttributesText(annotationIdx);
  if (!ellipse) {
    return null;
  }
  const vertices = getEllipseVertices(ellipse.data);
  const annotationStyle = getAnnotationStyle(ellipse.annotationLabel.style, {
    isFocused,
    isLocked,
    useStageStrokeWidth: true,
    isM4: isVideoLabelingM4Enabled
  });
  return <Shape vertices={vertices} isLocked={isLocked} isDrawing={isDrawing} metadataPillText={dynamicAttributesText} annotationLabel={{
    ...ellipse.annotationLabel,
    labelCoords
  }} annotationIdx={annotationIdx} onVertexSelect={({
    type
  }) => setSelectedVertex(type)} dataAttributes={{
    'data-e2e-id': `annotation-${ellipse.annotationLabel.title}-${ellipse.annotationType}`,
    'data-annotation-id': annotationIdx?.toString() || ''
  }}>
      <ellipse ref={ellipseRef} cx={ellipse.data.centerX} cy={ellipse.data.centerY} rx={ellipse.data.radiusX} ry={ellipse.data.radiusY} stroke={annotationStyle.strokeColor} fill={annotationStyle.fillColor} fillOpacity={annotationStyle.fillOpacity} style={{
      strokeWidth: annotationStyle.strokeWidth
    }} {...getStylesForStylingType(stylingType)} />
    </Shape>;
};
export default Ellipse;